import React, {useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {getListingGroups} from "redux/actions/listingsActions";
import ContextMenu from "connectui/component/input/context/ContextMenu";
import TextInput from "connectui/component/input/text/type/TextInput";
import FlexContainer from "connectui/component/layout/FlexContainer";
import Text from "connectui/component/display/Text";
import CheckBox from "connectui/component/input/CheckBox";
import Container from "connectui/component/layout/Container";
import {useTheme} from "connectui/theme/ThemeProvider";
import {buildStyles} from "connectui/component/buildStyles";
import SvgIcon from "connectui/component/display/SvgIcon";
import ICONS from "connectui/icons/iconMap";
import OutlinedInputBase from "connectui/component/input/text/OutlinedInputBase";
import ListComponent from "connectui/component/layout/list/ListComponent";
import Skeleton from "connectui/component/display/Skeleton";
import ButtonText from "connectui/component/input/button/ButtonText";
import OutlinedButtonBase from "connectui/component/input/button/base/OutlinedButtonBase";
import {useTranslation} from "react-i18next";

const useStyles = (theme) =>
  buildStyles({
    contextMenuContainer: {
      padding: theme.spacing(2, 2),
      minWidth: "300px",
      maxHeight: "400px",
      display: "flex",
      flexDirection: "column",
    },
    searchIcon: {
      width: "15px",
      height: "15px",
      color: theme.colors.textPrimary,
      marginRight: theme.spacing(2),
    },
    xIcon: {
      width: "11px",
      height: "11px",
      cursor: "pointer",
      color: theme.colors.gray500,
    },
    listContainer: {
      overflow: "auto",
      marginTop: theme.spacing(2),
    },
    listItem: {
      padding: theme.spacing(2.5, 3.5),
      cursor: "pointer",
      height: "100%",
      "&:hover": {
        backgroundColor: theme.colors.gray50,
      },
    },
  });

function ListingGroupSelectorMenu({
  open,
  onClose,
  anchorEl,
  selectedIds = [],
  onGroupSelection,
}) {
  const {theme} = useTheme();
  const dispatch = useDispatch();
  const classes = useStyles(theme);
  const [searchText, setSearchText] = useState("");
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const isMounted = useRef(false);
  const {t} = useTranslation();

  const listing_groups = useSelector(
    (state) => state.defaultReducer.listing_groups,
  );

  const availableGroups = listing_groups.filter(
    (group) => group.group_id !== "ALL",
  );

  const loadGroups = () => {
    setLoadingGroups(true);
    dispatch(getListingGroups());
    setLoadingGroups(false);
  };

  const handleSearchChange = (value) => {
    setSearchText(value);

    if (!isMounted.current) return;

    const filtered = availableGroups.filter((group) =>
      group.name?.toLowerCase().includes(value.toLowerCase()),
    );
    setFilteredGroups(filtered);
  };

  const handleSearchClear = () => {
    setSearchText("");
    setFilteredGroups(availableGroups);
  };

  useEffect(() => {
    if (anchorEl && !isMounted.current) {
      isMounted.current = true;
      loadGroups();
    }
  }, [anchorEl]);

  useEffect(() => {
    const filtered = searchText
      ? availableGroups.filter((group) =>
          group.name?.toLowerCase().includes(searchText.toLowerCase()),
        )
      : availableGroups;
    setFilteredGroups(filtered);
  }, [listing_groups, searchText]);

  const renderGroupsList = () => {
    if (loadingGroups) {
      return (
        <ListComponent
          gap={theme.spacing(1)}
          sx={{width: "100%", height: "100%"}}
        >
          {Array.from({length: 5}).map((_, index) => (
            <Skeleton
              key={index}
              width={"100%"}
              height={"44px"}
              style={{borderRadius: `${theme.radius.lg}px`}}
            />
          ))}
        </ListComponent>
      );
    }

    if (filteredGroups.length === 0) {
      return (
        <FlexContainer
          alignItems={"center"}
          justifyContent={"center"}
          flexDirection={"column"}
          gap={theme.spacing(2)}
          sx={{
            height: "100%",
            width: "100%",
          }}
        >
          <Text color={"textSecondary"} variant={"textMD"}>
            {searchText ? "No groups found" : "No listing groups available"}
          </Text>
          {searchText && (
            <ButtonText
              buttonBase={OutlinedButtonBase}
              label={t(`inbox.guest-details.followers.clear-query`)}
              onClick={handleSearchClear}
            />
          )}
        </FlexContainer>
      );
    }

    return (
      <ListComponent gap={theme.spacing(1)} sx={{width: "100%"}}>
        {filteredGroups.map((group) => {
          const isSelected = selectedIds.includes(group.group_id);
          const listingCount = group.connected_to?.ids?.length || 0;

          return (
            <FlexContainer
              key={group.group_id}
              alignItems="center"
              justifyContent="space-between"
              className={classes.listItem}
              onClick={() => onGroupSelection(group.group_id, isSelected)}
            >
              <Container>
                <Text variant="textSM">{group.name}</Text>
                <Text variant="textXS" color="textSecondary">
                  {listingCount === 1
                    ? `${listingCount} listing`
                    : `${listingCount} listings`}
                </Text>
              </Container>
              <CheckBox isChecked={isSelected} />
            </FlexContainer>
          );
        })}
      </ListComponent>
    );
  };

  return (
    <ContextMenu
      open={open}
      onClose={onClose}
      anchorEl={anchorEl}
      modalProps={{
        anchorOrigin: {
          horizontal: "start",
        },
      }}
      customContent={
        <Container className={classes.contextMenuContainer}>
          <TextInput
            inputBase={OutlinedInputBase}
            startIcon={
              <SvgIcon
                component={ICONS.search}
                className={classes.searchIcon}
                sx={{color: theme.colors.gray500}}
              />
            }
            inputProps={{
              placeholder: "Search listing groups",
              value: searchText,
              autoFocus: true,
              onChange: (e) => {
                handleSearchChange(e.target.value);
                e.stopPropagation();
              },
            }}
            actions={
              !!searchText && (
                <SvgIcon
                  component={ICONS.x}
                  className={classes.xIcon}
                  onClick={handleSearchClear}
                />
              )
            }
          />
          <FlexContainer
            flexDirection="column"
            className={classes.listContainer}
            sx={{
              height: "300px",
            }}
          >
            {renderGroupsList()}
          </FlexContainer>
        </Container>
      }
    />
  );
}

export default ListingGroupSelectorMenu;
